import React, { useState } from 'react'
import { Icon } from '@iconify/react'

import useUserContext from '../../../../hooks/useUserContext'
import API from '../../../../utils/api'

import styles from './verify-identity.module.css'
import phoneStyles from './phone.module.css'
import { toastSuccess } from '../../../Toast';

export const VerifyIdentity = ({ onSuccess }) => {
  const { updateUserInfo, userData } = useUserContext()
  const [step, setStep] = useState('start-step')
  const [isValidationConfirmed, setIsValidationConfirmed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [interviewSession, setInterviewSession] = useState(null) 

  const verifycationSuccess = async () => {
    if (userData?.user) {
      await updateUserInfo({
        ...userData,
        user: {
          ...userData.user,
          cashouts_verified: 4,
        },
      })
    } else {
      await updateUserInfo()
    }

    setStep('success-step')
    toastSuccess('Tu cuenta ha sido verificada exitosamente')
    onSuccess?.()
  }

  const handleSendSMS = async () => {
    const phone = String(userData?.user?.phone || phoneNumber)

    if (phone === '' || phone === null) {
      setIsLoading(false)
      setErrorMessage('Debes ingresar un número de teléfono')
      return
    }

    if (phone.length !== 10 && phone.length !== 12) {
      setIsLoading(false)
      setErrorMessage('El número de teléfono debe tener 10 dígitos')
      return
    }

    setIsValidationConfirmed(false)
    setIsLoading(true)
    setErrorMessage(null)

    const onBoarding = API.getIncodeOnBoarding()
    const confId = process.env.REACT_APP_INCODE_CONF

    try {
      const token = await onBoarding.createSession('MX', null, { configurationId: confId })
      const interviewSession = token
      const saveSessionParams = { type: 'cashout', session: interviewSession }

      const saveSessionResponse = await API.incodeSaveSession(saveSessionParams)
      if (!saveSessionResponse.data.success) {
        const message = saveSessionResponse?.data?.message || 'Error al guardar la sesión. Por favor intenta de nuevo.'
        setIsLoading(false)
        setErrorMessage(message)
        return
      }

      const smsParams = { phone, session: interviewSession }
      const smsResponse = await API.sendIncodeSms(smsParams)

      setInterviewSession(interviewSession)

      if (!smsResponse.data.res.success) {
        const message = smsResponse?.data?.res?.message || 'No se pudo enviar el SMS. Por favor intenta de nuevo.'
        setIsLoading(false)
        setErrorMessage(message)
        return
      }

      setStep('get-results')
      setIsLoading(false)
      setErrorMessage(null)
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Error al procesar la solicitud. Por favor intenta de nuevo.'
      setIsLoading(false)
      setErrorMessage(message)
    }
  }

  const getIncodeResults = () => {
    setIsLoading(true)

    API.getIncodeResults(interviewSession).then((response) => {
      let message = ''
      let success = false

      switch (response.data.result) {
        case null:
          message = 'No se pudo realizar la verificación, intentalo de nuevo'

          break
        case 'OK':
          message = 'Tu verificación ha sido exitosa'
          success = true
          break
        case 'MANUAL':
        case 'WARN':
          message = 'No pudimos verificar tus documentos, el equipo de pickwin revisará tu caso. para cualquier aclaración comunicate a soporte@pickwin.net'
          break
        case 'FAIL':
          message = 'No se pudo verificar tu cuenta, por favor comunicate a soporte@pickwin.net o intentalo de nuevo'
          break
        default:
          message = 'No pudimos verificar tu identidad, intentalo nuevamente'
      }

      setIsLoading(false)
      setErrorMessage(message)

      if (success) {
        verifycationSuccess()
      } else {
        setStep('error-step')
      }
    })
  }

  const resetStep = () => {
    setStep('start-step')
    setIsValidationConfirmed(false)
    setIsLoading(false)
    setErrorMessage(null)
    setPhoneNumber('')
  }

  const handlePhoneChange = (event) => {
    const value = event.target.value
    const cleaned = value.replace(/\D/g, '') // solo números
    setPhoneNumber(cleaned)
  }

  const handlePhoneKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      event.preventDefault()
      handleSendSMS()
    }
  }

  if (step === 'start-step') {
    return (
      <div>
        <div className={styles.verifyIdentityContainer}>
          <p className={styles.verifyIdentityText}>Para continuar, valida tu identidad mediante SMS.</p>
          <p>Abre el enlace que te enviaremos por SMS y sigue los pasos en tu celular.</p>
          <p>Al finalizar, revisa aquí tu resultado.</p>
          <p className={styles.requirementsTitle}>
            <Icon icon="carbon:warning-hex-filled" marginRight={10} /> <span>Ten a la mano:</span>
          </p>
          <ul className={styles.requirementsList}>
            <li className={styles.requirementItem}>
              <span className={styles.requirementIcon}>
                <Icon icon="mdi:card-account-details-outline" />
              </span>
              <span>Identificación oficial vigente (INE o pasaporte)</span>
            </li>
            <li className={styles.requirementItem}>
              <span className={styles.requirementIcon}>
                <Icon icon="mdi:file-document-outline" />
              </span>
              <span>Comprobante de domicilio</span>
            </li>
          </ul>

          {!userData?.user?.phone && (
            <div className={phoneStyles.field}>
              <label className={phoneStyles.label} htmlFor="phone-number">
                Número de teléfono
              </label>
              <div className={phoneStyles.shell}>
                <span className={phoneStyles.prefix}>
                  <Icon icon="bi:phone" />
                </span>
                <input
                  maxLength={10}
                  inputMode="numeric"
                  autoFocus
                  className={phoneStyles.input}
                  type="text"
                  placeholder="Número de teléfono"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                />
              </div>
            </div>
          )}

          {errorMessage && (
            <div className={styles.errorMessage}>
              {errorMessage}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className='payment-button' onClick={handleSendSMS} disabled={isLoading}>
            {isLoading ? 'Enviando SMS...' : 'Enviar SMS'}
          </button>
        </div>
      </div>
    )
  }

  if (step === 'get-results') {
    return (
      <div>
        <div className={styles.verifyIdentityContainer}>
          <p className={styles.verifyIdentityText}>Se ha enviado un SMS a tu número de teléfono.</p>
          <p>Abre el enlace que te enviaremos por SMS y sigue los pasos en tu celular.</p>
          <p>Al finalizar confirma y presiona en  <span className={styles.textValidation}>Obtener resultados de validación.</span></p>
        </div>

        <div className={styles.footerSmsStep}>
          <label className={styles.confirmationCheckbox}>
            <input
              type="checkbox"
              checked={isValidationConfirmed}
              onChange={(event) => setIsValidationConfirmed(event.target.checked)}
            />
            <span className={styles.checkmark} />
            <span>Confirmo que he terminado el proceso de verificación en mi celular</span>
          </label>

          <button
            className='payment-button'
            onClick={getIncodeResults}
            disabled={!isValidationConfirmed || isLoading}
          >
            {isLoading ? 'Obteniendo resultados...' : 'Obtener resultados de validación'}
          </button>
        </div>
      </div>
    )
  }

  if (step === 'error-step') {
    return (
      <div className={styles.resultContainer}>
        <p className={styles.errorMessage}>{errorMessage}</p>

        <div className={styles.footer}>
          <button className='payment-button' onClick={resetStep} disabled={isLoading}>
            Volver a intentar
          </button>
        </div>
      </div>
    )
  }

  if (step === 'success-step') {
    return (
      <div className={styles.resultContainer}>
        <p className={styles.successMessage}>Tu cuenta ha sido verificada exitosamente</p>
      </div>
    )
  }

  return null
}
